import React from 'react';
import { Box, Button, Grid, IconButton, Modal, Tooltip, Typography } from '@mui/material';
import { User } from 'src/interfaces/iuser';
import { useSelector } from 'react-redux';
import { MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY } from 'src/const';
import CloseIcon from '@mui/icons-material/Close';
import { useGetCreditsRemaining } from 'src/hooks/useCustomHooks';
import { useCanAccessScalePlan } from 'src/hooks/useFeatureAccess';

const MarketExportModal = ({
  open,
  handleClose,
  handleExportRows,
  handleOnSampleClick,
  numberOfRows,
  handleExportRowsFree,
  setCreditDeductModalOpen,
  currentRequestType
}: {
  open: boolean;
  handleClose: () => void;
  handleExportRows: (isPremium: boolean) => void;
  handleOnSampleClick: (planName: string) => void;
  numberOfRows: number;
  handleExportRowsFree: () => void;
  setCreditDeductModalOpen: (open: boolean) => void;
  currentRequestType: 'free' | 'premium';
}) => {
  const user: User = useSelector((state: any) => state.dashboard.user);
  // const currentYearMonthString = new Date().toISOString().slice(0, 7);
  // const creditsUsed = user.marketExplorerRowDataExportUsage[currentYearMonthString] || 0;
  // const creditsRemaining = MARKET_EXPLORER_EXPORT_LIMIT_ROW_MONTHLY - creditsUsed;

  const isScaleUser = useCanAccessScalePlan();
  const { creditsRemaining, creditsUsed } = useGetCreditsRemaining();

  const handleUseCredits = () => {
    handleExportRows(true);
    handleClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '500px',
          backgroundColor: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: 24,
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <IconButton onClick={handleClose} sx={{ position: 'absolute', top: '5px', right: '5px' }}>
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" sx={{ fontSize: '22px' }}>
          Export Market Data (
          {numberOfRows > 200 ? '200 rows' : `${Math.max(Math.min(creditsRemaining, numberOfRows), 0)} rows`})
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '16px', textAlign: 'center', mt: '10px' }}>
          {!isScaleUser && 'Get a preview with limited columns or unlock full access by upgrading.'}
          {isScaleUser &&
            creditsRemaining >= numberOfRows &&
            'Export the full dataset with all available metrics.'}
          {isScaleUser && creditsRemaining < numberOfRows && creditsRemaining > 0 && (
            <>
              You need {numberOfRows} credits to download this dataset,
              but you only have {creditsRemaining} remaining this month.
            </>
          )}
          {isScaleUser && creditsRemaining === 0 && (
            <>
              You have reached the Scale plan limit for Export. Please wait until your limit resets
              on next month.
            </>
          )}
        </Typography>
        <Grid container spacing={2} justifyContent="center" sx={{ mt: '10px' }}>
          <Grid item>
            <Tooltip title="Preview with selected columns">
              <Button
                variant="outlined"
                color="secondary"
                sx={{ width: '100%' }}
                onClick={handleExportRowsFree}
              >
                Download Sample CSV
              </Button>
            </Tooltip>
          </Grid>
          <Grid item>
            <Tooltip title="Access full dataset with all metrics">
              <Button
                variant="contained"
                color="secondary"
                disabled={Math.min(creditsRemaining, numberOfRows) < 1}
                onClick={() => {
                  if (user.subscriptionPlan === 'scale') {
                    if (currentRequestType === 'free') {
                      setCreditDeductModalOpen(true);
                    }else{
                      handleUseCredits();
                    }
                  } else {
                    handleOnSampleClick('scale');
                  }
                }}
                sx={{ width: '100%', color: 'white' }}
              >
                {user.subscriptionPlan === 'scale' ? (
                  currentRequestType === 'premium' ? (
                    <>Download Full CSV ({Math.max(Math.min(creditsRemaining, numberOfRows), 0)} credits)</>
                  ) : (
                    <>Unlock the Market</>
                  )
                ) : (
                  <>Upgrade to Scale Plan</>
                )}
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
};

export default MarketExportModal;
